import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Zap, Clapperboard, Heart, MessageCircle, MoreHorizontal,
  UserPlus, Ban, EyeOff, Loader2, Send, X, Play,
} from "lucide-react";
import { socialApi, resolveMedia } from "@/api/social";
import { useSession } from "@/context/SessionContext";

const PAGE = 10;

function CardMenu({ post, onFollow, onBlock, onNotInterested, onClose }) {
  return (
    <div className="absolute right-2 top-10 z-30 w-44 bg-white border-2 border-charcoal rounded-xl shadow-[3px_3px_0px_#1A1A1A] overflow-hidden" data-testid={`card-menu-${post.id}`}>
      {!post.is_me && (
        <>
          <button onClick={() => { onFollow(post); onClose(); }} data-testid={`menu-follow-${post.id}`} className="w-full flex items-center gap-2 px-3 py-2.5 font-mono text-[11px] text-charcoal border-b border-charcoal/10 active:bg-cream">
            <UserPlus size={14} /> {post.following ? "Unfollow User" : "Follow User"}
          </button>
          <button onClick={() => { onBlock(post); onClose(); }} data-testid={`menu-block-${post.id}`} className="w-full flex items-center gap-2 px-3 py-2.5 font-mono text-[11px] text-red-600 border-b border-charcoal/10 active:bg-cream">
            <Ban size={14} /> Block Channel
          </button>
        </>
      )}
      <button onClick={() => { onNotInterested(post); onClose(); }} data-testid={`menu-notinterested-${post.id}`} className="w-full flex items-center gap-2 px-3 py-2.5 font-mono text-[11px] text-charcoal active:bg-cream">
        <EyeOff size={14} /> Not Interested
      </button>
    </div>
  );
}

function CommentSheet({ post, onClose }) {
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => socialApi.comments(post.id).then((r) => setItems(r.comments || [])).catch(() => setItems([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try { await socialApi.addComment(post.id, text.trim()); setText(""); await load(); }
    catch { toast.error("Could not comment"); } finally { setBusy(false); }
  };
  return (
    <div className="absolute inset-0 z-40 bg-black/70 flex items-end" data-testid="comment-sheet">
      <div className="w-full bg-cream border-t-4 border-charcoal rounded-t-3xl p-4 max-h-[80dvh] flex flex-col">
        <div className="flex items-center gap-2 pb-2 border-b-2 border-charcoal/10 mb-2">
          <h3 className="font-serif text-xl text-charcoal flex-1">Comments</h3>
          <button onClick={onClose} data-testid="comment-close" className="w-8 h-8 grid place-items-center rounded-lg border-2 border-charcoal bg-white shadow-[1.5px_1.5px_0px_#1A1A1A]"><X size={15} /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-2">
          {items == null ? <div className="grid place-items-center py-6"><Loader2 size={18} className="animate-spin text-charcoal/40" /></div>
            : items.length === 0 ? <p className="font-mono text-[10px] text-charcoal/40 py-4 text-center">Be the first to comment.</p>
            : items.map((c) => (
              <div key={c.id} className="bg-white border-2 border-charcoal rounded-lg px-2.5 py-1.5 shadow-[2px_2px_0px_#1A1A1A]">
                <p className="font-mono text-[8px] uppercase tracking-widest text-charcoal/40">{c.author_name || c.author_id}</p>
                <p className="font-mono text-[11px] text-charcoal">{c.text}</p>
              </div>
            ))}
        </div>
        <div className="flex items-center gap-2 pt-2">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="comment-input" placeholder="Add a comment…" className="flex-1 bg-white border-2 border-charcoal rounded-full px-3 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-gold shadow-[2px_2px_0px_#1A1A1A]" />
          <button onClick={send} disabled={busy} data-testid="comment-send" className="w-10 h-10 rounded-full bg-gold border-2 border-charcoal grid place-items-center shadow-[2px_2px_0px_#1A1A1A] disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} className="text-charcoal" />}</button>
        </div>
      </div>
    </div>
  );
}

function FeedCard({ post, token, onOpenReels, onFollow, onBlock, onNotInterested }) {
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likes, setLikes] = useState(post.likes || 0);
  const [menu, setMenu] = useState(false);
  const [comments, setComments] = useState(false);
  const media = post.media?.[0];
  const mediaUrl = media ? resolveMedia(media.url, token) : null;
  const audioMedia = post.media?.find((m) => m.kind === "audio");
  const audioUrl = audioMedia ? resolveMedia(audioMedia.url, token) : null;

  const like = async () => {
    setLiked((v) => !v); setLikes((n) => n + (liked ? -1 : 1));
    try { const r = await socialApi.like(post.id); setLiked(r.liked); setLikes(r.likes); } catch { /* revert on next load */ }
  };

  return (
    <div className="relative border-4 border-charcoal rounded-2xl bg-white shadow-hard overflow-hidden" data-testid={`feed-card-${post.id}`}>
      {/* header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b-2 border-charcoal/10">
        <span className="w-8 h-8 rounded-full bg-charcoal grid place-items-center text-gold font-mono text-[10px] font-black overflow-hidden shrink-0">
          {post.author_avatar ? <img src={resolveMedia(post.author_avatar, token)} alt="" className="w-full h-full object-cover" /> : (post.author_name || "?").slice(0, 2).toUpperCase()}
        </span>
        <p className="font-mono text-[11px] font-bold text-charcoal flex-1 truncate">{post.author_name}</p>
        <button onClick={() => setMenu((m) => !m)} data-testid={`card-more-${post.id}`} className="w-8 h-8 grid place-items-center rounded-lg active:bg-cream"><MoreHorizontal size={18} className="text-charcoal" /></button>
      </div>
      {menu && <CardMenu post={post} onFollow={onFollow} onBlock={onBlock} onNotInterested={onNotInterested} onClose={() => setMenu(false)} />}

      {/* body — tap jumps into Explore reels */}
      <button onClick={() => onOpenReels(post)} data-testid={`card-body-${post.id}`} className="block w-full text-left">
        {/* STRICT 16:9 WIDESCREEN LETTERBOX SANDBOX — prevents layout stretching / variable heights */}
        {mediaUrl && (
          <div className="relative w-full aspect-video bg-[#121212] border-y-2 border-charcoal/10 flex items-center justify-center overflow-hidden" data-testid={`card-media-frame-${post.id}`}>
            {media.kind === "image" && <img src={mediaUrl} alt="" className="w-full h-full object-contain bg-black" />}
            {media.kind === "video" && (
              <>
                <video src={mediaUrl} muted playsInline className="w-full h-full object-contain bg-black" />
                <span className="absolute inset-0 grid place-items-center pointer-events-none"><Play size={36} className="text-white/90" fill="white" /></span>
              </>
            )}
            <span className="absolute top-2 right-2 bg-black/70 backdrop-blur-md px-1.5 py-0.5 rounded text-[7px] tracking-widest text-zinc-400 uppercase font-mono border border-zinc-700/40 pointer-events-none">
              16:9
            </span>
          </div>
        )}
        {post.text && <p className="px-3 py-2.5 font-mono text-[12px] text-charcoal leading-snug">{post.text}</p>}
      </button>

      {/* inline audio player — cover art above acts as the visual background */}
      {audioUrl && (
        <div className="px-3 py-2 border-t-2 border-charcoal/10" data-testid={`card-audio-wrap-${post.id}`}>
          <audio src={audioUrl} controls className="w-full h-9" data-testid={`card-audio-${post.id}`} />
        </div>
      )}

      {/* actions */}
      <div className="flex items-center gap-4 px-3 py-2 border-t-2 border-charcoal/10">
        <button onClick={like} data-testid={`card-like-${post.id}`} className="flex items-center gap-1.5 active:scale-95">
          <Heart size={18} className={liked ? "text-red-500" : "text-charcoal"} fill={liked ? "#ef4444" : "none"} />
          <span className="font-mono text-[11px] font-bold text-charcoal tabular-nums">{likes}</span>
        </button>
        <button onClick={() => setComments(true)} data-testid={`card-comment-${post.id}`} className="flex items-center gap-1.5 active:scale-95">
          <MessageCircle size={18} className="text-charcoal" />
          <span className="font-mono text-[11px] font-bold text-charcoal tabular-nums">{post.comment_count || 0}</span>
        </button>
      </div>
      {comments && <CommentSheet post={post} onClose={() => setComments(false)} />}
    </div>
  );
}

export default function GlobalFeed({ onBack, onAddMedia, onOpenReels }) {
  const { token } = useSession();
  const [posts, setPosts] = useState(null);
  const [shown, setShown] = useState(PAGE);
  const [total, setTotal] = useState(0);

  const load = () => socialApi.globalFeed(0, 50).then((r) => { setPosts(r.posts); setTotal(r.total); }).catch(() => setPosts([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const follow = async (p) => { try { const r = await socialApi.follow(p.author_id); toast.success(r.following ? "Following" : "Unfollowed"); load(); } catch { toast.error("Failed"); } };
  const block = async (p) => { try { await socialApi.block(p.author_id); toast("Channel blocked", { icon: "🚫" }); load(); } catch { toast.error("Failed"); } };
  const notInterested = async (p) => { setPosts((ps) => ps.filter((x) => x.id !== p.id)); try { await socialApi.notInterested(p.id); } catch { load(); } };

  const visible = (posts || []).slice(0, shown);
  const canLoadMore = (posts || []).length > shown;

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-cream" data-testid="global-feed">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3">
        <button onClick={onBack} data-testid="feed-back-btn" aria-label="Back" className="w-10 h-10 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} strokeWidth={2.4} /></button>
        <div className="leading-none">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-gold">Social Hub</p>
          <h1 className="font-serif text-2xl text-charcoal">Global Feed</h1>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-6 space-y-3">
        {/* Add Story / Add Reel — redirect to Mist Director */}
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => onAddMedia?.("story")} data-testid="add-story-tile" className="border-4 border-charcoal rounded-2xl bg-[#E8D3FF] p-3 flex flex-col items-start gap-1.5 shadow-[4px_4px_0px_#1A1A1A] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none">
            <Zap size={22} className="text-charcoal" />
            <span className="font-serif text-lg text-charcoal leading-none">Add Story</span>
          </button>
          <button onClick={() => onAddMedia?.("reel")} data-testid="add-reel-tile" className="border-4 border-charcoal rounded-2xl bg-[#A3E2FF] p-3 flex flex-col items-start gap-1.5 shadow-[4px_4px_0px_#1A1A1A] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none">
            <Clapperboard size={22} className="text-charcoal" />
            <span className="font-serif text-lg text-charcoal leading-none">Add Reel</span>
          </button>
        </div>

        {posts == null ? (
          <div className="grid place-items-center py-12"><Loader2 size={22} className="text-charcoal/40 animate-spin" /></div>
        ) : visible.length === 0 ? (
          <p className="text-center font-mono text-[11px] text-charcoal/50 pt-10">No posts in the timeline yet.</p>
        ) : (
          <>
            {visible.map((p) => (
              <FeedCard key={p.id} post={p} token={token} onOpenReels={onOpenReels} onFollow={follow} onBlock={block} onNotInterested={notInterested} />
            ))}
            {canLoadMore && (
              <button onClick={() => setShown((s) => s + PAGE)} data-testid="load-more-btn" className="w-full border-4 border-dashed border-charcoal/40 rounded-2xl py-3.5 font-mono text-[11px] font-black uppercase tracking-widest text-charcoal/70 active:bg-white">
                Load More Timeline Threads
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
